import { AbsoluteFill, Audio, Freeze, OffthreadVideo, Sequence } from "remotion";
import { CaptionLine } from "./CaptionLine.tsx";
import { CoverFrame } from "./CoverFrame.tsx";
import { FontFaces } from "./fonts.tsx";
import { LogoMark } from "./LogoMark.tsx";
import { resolveSrc } from "./mediaSrc.ts";
import { SpeakerCards } from "./SpeakerCard.tsx";
import { TextOverlays } from "./TextOverlay.tsx";
import { DEFAULT_VIDEO_DIM, FPS, type ClipProps } from "./types.ts";
import { videoFilterStyle } from "./videoFilter.ts";

export const ClipComposition = (props: ClipProps): React.JSX.Element => {
  // videoSrc is a relative path under public/ (e.g. "clips/foo.mov") in the
  // render pipeline, or a server URL (/api/…) in the browser Studio preview.
  const src = resolveSrc(props.videoSrc);
  // Music starts at musicStartOffsetSec, converted to frames. Loop
  // restarts from this offset each cycle — keeps the operator-picked
  // "good part" of the song on repeat for clips that outlive the
  // remaining track. Clamped to ≥0 defensively in case malformed
  // music.json sneaks through.
  const musicStartFrames = Math.max(
    0,
    Math.round((props.musicStartOffsetSec ?? 0) * FPS),
  );
  // Darkening strength: a Studio edit (layout.video.dim) wins over the
  // deployment's QL_VIDEO_DIM (inputProps.videoDim); absent → founding default.
  const videoDim = props.layout?.video?.dim ?? props.videoDim ?? DEFAULT_VIDEO_DIM;
  // Footage lighting (layout.video.brightness / contrast): a CSS filter on the
  // video element itself, where `dim` only washes black over it. Spread, not
  // passed: absent/identity yields {} so the element carries NO style prop and
  // an unlit clip renders byte-identically. See ./videoFilter.ts.
  const lighting = videoFilterStyle(props.layout?.video);
  const content = (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <FontFaces faces={props.fontFaces} />
      <OffthreadVideo src={src} {...lighting} />
      {/* Freeze-open (QL_OPEN_FACE_HOLD): when the source opens on a B-roll
          cutaway, a frozen MUTED copy of the first speaker-visible frame
          covers the opening beat. The base video above keeps playing
          underneath with its untouched audio — zero A/V-sync surface; 0 or
          absent renders nothing, so the legacy tree is byte-identical. */}
      {(props.freezeOpenUntilSec ?? 0) > 0 && (
        <Sequence durationInFrames={Math.round(props.freezeOpenUntilSec! * FPS)}>
          <AbsoluteFill>
            <Freeze frame={Math.round(props.freezeOpenUntilSec! * FPS)}>
              <OffthreadVideo src={src} muted {...lighting} />
            </Freeze>
          </AbsoluteFill>
        </Sequence>
      )}
      {/* Darkening layer so logo + captions read clearly over bright footage.
          Designable via layout.video.dim (Studio); absent → QL_VIDEO_DIM / founding. */}
      <AbsoluteFill style={{ backgroundColor: `rgba(0,0,0,${videoDim})`, pointerEvents: "none" }} />
      {props.musicSrc && (
        <Audio
          src={resolveSrc(props.musicSrc)}
          volume={props.musicVolume ?? 0.14}
          startFrom={musicStartFrames}
          loop
        />
      )}
      <SpeakerCards speakers={props.speakers} card={props.layout?.speakerCard} />
      <CaptionLine cues={props.cues} brand={props.brand} layout={props.layout} />
      <LogoMark
        src={props.logoSrc}
        widthPct={props.layout?.logo.widthPct}
        bottomPx={props.layout?.logo.bottomPx}
        opacity={props.layout?.logo.opacity}
        xPct={props.layout?.logo.xPct}
      />
      {/* Timed text overlays (brand CTA). Inside the content tree, so a
          cover-hold prefix shifts them with everything else. Absent → null. */}
      <TextOverlays
        overlays={props.layout?.overlays}
        brand={props.brand}
        durationSec={props.durationSec}
      />
    </AbsoluteFill>
  );

  // Feed cover-hold (QL_COVER_HOLD): prepend a frozen branded cover and
  // shift the ENTIRE content tree — video with its speech track, music,
  // captions, speaker cards — through ONE <Sequence>, so every relative
  // timing inside the content is untouched by construction. coverHoldSec
  // absent/0 → the exact tree above renders, byte-identical to V1.
  const coverFrames = Math.round((props.coverHoldSec ?? 0) * FPS);
  if (coverFrames <= 0) return content;
  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Sequence durationInFrames={coverFrames}>
        <CoverFrame
          videoSrc={src}
          captionText={props.coverCaptionText}
          speakerPng={props.coverSpeakerPng}
          speakerPngWidthPx={props.coverSpeakerPngWidthPx}
          logoSrc={props.logoSrc}
          brand={props.brand}
          layout={props.layout}
          fontFaces={props.fontFaces}
          videoDim={videoDim}
        />
      </Sequence>
      <Sequence from={coverFrames}>{content}</Sequence>
    </AbsoluteFill>
  );
};
